import * as THREE from 'three';
import { FileLoader } from 'three';
import type { DataFormat, Field } from '../../types';
import { PointsAttributeBuffer } from '../PointsAttributeBuffer';

// Loads binary file xyz:3f,rgb:3ub,id:ui

export interface BinPoints {
  positionData: THREE.Float32BufferAttribute;
  attributes: {
    name: string;
    data: THREE.Float32BufferAttribute;
    attributeBuffer?: PointsAttributeBuffer;
    isColor: boolean;
  }[];
}

export const defaultMaxId = 1024 * 1024 - 1;

const currentDataFormatsByUrl: { [key: string]: DataFormat } = {};

// old format (still supported)
// export const defaultFields: Field[] = [
//   { name: 'position', offset: 0 },
//   { name: 'color', offset: 12 },
//   { name: 'scan', offset: 15 },
// ];

// Supports old and new formats
// Maps x,y,z => position
// Maps r,g,b => color
export const prepareAttributeFormats = (fields: Field[]) => {
  const fieldMap = new Map(fields.map((field): [string, Field] => [field.name, field]));

  const attributeFormats = new Array<{ name: string; fields: Field[] }>();

  const x = fieldMap.get('x');
  const y = fieldMap.get('y');
  const z = fieldMap.get('z');

  const position = fieldMap.get('position');

  if (x && y && z) {
    attributeFormats.push({ name: 'position', fields: [x, y, z] });
    fieldMap.delete('x');
    fieldMap.delete('y');
    fieldMap.delete('z');
  } else if (position) {
    attributeFormats.push({
      name: 'position',
      fields: [
        { name: 'x', type: 'f', offset: position.offset },
        { name: 'y', type: 'f', offset: position.offset + 4 },
        { name: 'z', type: 'f', offset: position.offset + 8 },
      ],
    });
    fieldMap.delete('position');
  } else {
    throw new Error(
      "Position field not found, a 'position' field or ('x','y','z' fields) are missing"
    );
  }

  const r = fieldMap.get('r');
  const g = fieldMap.get('g');
  const b = fieldMap.get('b');

  const color = fieldMap.get('color');

  if (r && g && b) {
    attributeFormats.push({ name: 'color', fields: [r, g, b] });
    fieldMap.delete('r');
    fieldMap.delete('g');
    fieldMap.delete('b');
  } else if (color) {
    attributeFormats.push({
      name: 'color',
      fields: [
        { name: 'r', type: 'ub', offset: color.offset },
        { name: 'g', type: 'ub', offset: color.offset + 1 },
        { name: 'b', type: 'ub', offset: color.offset + 2 },
      ],
    });
    fieldMap.delete('color');
  }

  fieldMap.forEach((field: Field, name: string) => {
    attributeFormats.push({ name, fields: [field] });
  });

  return attributeFormats;
};

export const parseBinPoints = (
  inputData: ArrayBuffer,
  url: string,
  dataFormat: DataFormat
): BinPoints => {
  const dataview = new DataView(inputData);

  const isLittleEndian = true;

  // console.log({ url, data, byteLength: data.byteLength });

  const positionCardinality = 3;
  const colorCardinality = 3;

  const { recordSize } = dataFormat;

  const attributeFormats = prepareAttributeFormats(dataFormat.fields);

  const numberPoints = Math.floor(dataview.byteLength / recordSize);

  if (numberPoints * recordSize !== dataview.byteLength) {
    throw new Error(
      `${url} file length ${dataview.byteLength} is not multiply of recordSize=${recordSize}`
    );
  }

  let positionFieldIndex = -1;

  const color = new THREE.Color();

  const attributeBuffers = new Array<{
    name: string;
    cardinality: number;
    data: Float32Array;
    attributeBuffer?: PointsAttributeBuffer;
    isColor: boolean;
    copyValue: (pointIndex: number, offset: number) => void;
  }>();

  attributeFormats.forEach(({ name, fields }) => {
    if (name === 'position') {
      // point coordinates (xyz, float[3])
      if (positionFieldIndex !== -1) {
        throw new Error(`${url} duplicate field name 'position' in fields`);
      }
      positionFieldIndex = attributeBuffers.length;

      fields.forEach((field) => {
        if (field.type && field.type !== 'f') {
          throw new Error(
            `${url} unsupported position field type '${field.type}', field '${field.name}'`
          );
        }
      });

      const positionData = new Float32Array(numberPoints * positionCardinality);

      attributeBuffers.push({
        name,
        cardinality: positionCardinality,
        data: positionData,
        isColor: false,
        copyValue: (pointIndex: number, recordOffset: number) => {
          const destinationIndex = pointIndex * positionCardinality;

          [...Array(3).keys()].forEach((index) => {
            positionData[destinationIndex + index] = dataview.getFloat32(
              recordOffset + fields[index].offset,
              isLittleEndian
            );
          });
        },
      });
      return;
    }
    if (name === 'color') {
      // color (rgb, uint8[3])
      fields.forEach((field) => {
        if (field.type && field.type !== 'ub') {
          throw new Error(
            `${url} unsupported color field type '${field.type}', field '${field.name}'`
          );
        }
      });

      const colorData = new Float32Array(numberPoints * colorCardinality);

      attributeBuffers.push({
        name,
        cardinality: colorCardinality,
        data: colorData,
        isColor: true,
        copyValue: (pointIndex: number, recordOffset: number) => {
          const r = dataview.getUint8(recordOffset + fields[0].offset);
          const g = dataview.getUint8(recordOffset + fields[1].offset);
          const b = dataview.getUint8(recordOffset + fields[2].offset);

          // color.setRGB(r / 255, g / 255, b / 255);
          color.setHex(r * 0x10000 + g * 0x100 + b);

          const destinationIndex = pointIndex * colorCardinality;
          colorData[destinationIndex + 0] = color.r;
          colorData[destinationIndex + 1] = color.g;
          colorData[destinationIndex + 2] = color.b;
        },
      });
      return;
    }
    // id attribute (int8, int16, int32, ...)

    if (fields.length !== 1) {
      throw new Error('unexpected attribute fileds length');
    }
    const field = fields[0];

    const fieldGetters = new Map<string, (offset: number) => number>([
      ['ui', (offset: number) => dataview.getUint32(offset, isLittleEndian)],
      ['i', (offset: number) => dataview.getInt32(offset, isLittleEndian)],
      ['uw', (offset: number) => dataview.getUint16(offset, isLittleEndian)],
      ['w', (offset: number) => dataview.getInt16(offset, isLittleEndian)],
      ['ub', (offset: number) => dataview.getUint8(offset)],
      ['b', (offset: number) => dataview.getInt8(offset)],
    ]);

    const fieldGetter = fieldGetters.get(field.type ?? 'ui');

    if (fieldGetter === undefined) {
      throw new Error(
        `${url} unsupported attribute field type '${field.type}', field '${field.name}'`
      );
    }

    const maxId = field.maxId ?? defaultMaxId;

    const attributeData = new PointsAttributeBuffer(maxId, numberPoints);

    attributeBuffers.push({
      name,
      cardinality: attributeData.cardinality,
      data: attributeData.data,
      attributeBuffer: attributeData,
      isColor: false,
      copyValue: (pointIndex: number, recordOffset: number) => {
        const id = fieldGetter(recordOffset + field.offset);
        attributeData.setValue(pointIndex, id);
      },
    });
  });
  if (positionFieldIndex === -1) {
    throw new Error(`${url} missing field 'position'`);
  }

  // copying data into new buffers
  for (let pointIndex = 0; pointIndex < numberPoints; pointIndex += 1) {
    const recordOffset = pointIndex * recordSize;

    attributeBuffers.forEach((attributeBuffer) => {
      attributeBuffer.copyValue(pointIndex, recordOffset);
    });
  }

  const attributes = attributeBuffers.map(
    ({ name, data, attributeBuffer, cardinality, isColor }) => {
      return {
        name,
        data: new THREE.Float32BufferAttribute(data, cardinality),
        attributeBuffer,
        isColor,
      };
    }
  );

  const positionData = attributes.splice(positionFieldIndex, 1)[0].data;

  return { positionData, attributes };
};

export class BinPointsLoader extends THREE.Loader<BinPoints, string> {
  // temporary solution to overcome react-three-fiber limitations (unable to use loader instance in useLoader),
  // it's probably fixed in new versions, but they require React 19  //
  setDataFormat(url: string, dataFormat: DataFormat) {
    const octreePath = url.substring(0, url.lastIndexOf('/'));
    currentDataFormatsByUrl[octreePath] = dataFormat;
  }

  load(
    url: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onLoad: (argument0: any) => void,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onProgress: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (argument0: any) => void
  ) {
    // THREE.Cache.enabled = true;

    const { manager, requestHeader, withCredentials } = this;

    const loader = new FileLoader(manager);

    loader.setResponseType('arraybuffer');

    loader.setRequestHeader(requestHeader);
    loader.setWithCredentials(withCredentials);
    loader.load(
      url,
      (data) => {
        try {
          const octreePath = url.substring(0, url.lastIndexOf('/'));
          const dataFormat = currentDataFormatsByUrl[octreePath];
          if (!dataFormat) {
            throw new Error('dataFormat not found');
          }

          onLoad(parseBinPoints(data as ArrayBuffer, url, dataFormat));
        } catch (error) {
          if (onError) {
            onError(error);
          } else {
            console.error(error);
          }

          this.manager.itemError(url);
        }
      },
      onProgress,
      onError
    );
  }
}
